import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { parse } from 'date-fns'
import countries from 'i18n-iso-countries'
import i18n from 'i18next'
import {
  getDeviceComposition,
  getDeviceRoles,
  updateServiceInterval,
} from '@/api/calibration'
import { patchDevice } from '@/api/Device'

import useResponseValidator from '@/pages/utils/responseValidator'
import type { Device } from '@/pages/Device/Device'

import { RDI_3000, RSS_3000, RSS_3000_PC } from '@/pages/Device/SupportedTypes'
import { DeviceRole, DeviceRoles } from '@/pages/calibration/calibration'
import { Composition } from '@/pages/calibration/composition'
import {
  deviceHasBatteryBoard,
  getMinInstallationDate,
  updateBatteryBoardDate,
} from '@/pages/calibration/batterBoard'
import {
  CompositionSchema,
  DeviceRolesSchema,
} from '@/pages/calibration/schema'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

export const DATE_FORMAT = 'yyyy-MM-dd'

export function getSupportedCountries(deviceType: string): string[] {
  if (deviceType === RSS_3000_PC) return ['DE']
  if (deviceType === RSS_3000) return ['GB']
  if (deviceType === RDI_3000) return ['FR', 'BE']
  return ['NL', 'GB', 'FR', 'BE', 'AU']
}

/**
 * Resolves which role the pre-flight form should hold.
 *
 * A device type offering a single role has that role predefined, so it is
 * always applied - a role stored on the composition that the type no longer
 * offers (a device that changed type, for instance) would otherwise leave the
 * field blank, because the select has no matching option. With several roles
 * to choose from the stored role is kept, and the engineer picks one when it
 * is absent or no longer offered.
 */
export function resolveDeviceRole(
  deviceRoles: DeviceRole[],
  compositionDeviceRoleCode: string,
): string {
  if (deviceRoles.length === 0) return ''
  if (deviceRoles.length === 1) return deviceRoles[0].deviceRoleCode
  return deviceRoles.some(
    (role) => role.deviceRoleCode === compositionDeviceRoleCode,
  )
    ? compositionDeviceRoleCode
    : ''
}

export interface PreFlightSubmitOptions {
  country: string
  deviceRoleCode?: string
  yearsBetweenMaintenance?: string
  installedDate?: string
  onExtraSubmit?: () => Promise<void>
}

export interface PreFlightBaseResult {
  composition: Composition | null
  refreshComposition: () => void
  deviceRoles: DeviceRole[]
  deviceRolesLoading: boolean
  isSaving: boolean
  effectiveCountry: string
  effectiveYears: number
  countryItems: { id: string; label: string }[]
  defaultDeviceRole: string
  deviceRoleRequired: boolean
  hasBoard: boolean
  minInstalledDate: Date
  submitForm: (options: PreFlightSubmitOptions) => Promise<void>
  handleCountryChange: (country: string) => void
  handleYearsIntervalChange: (years: number) => void
}

export function usePreFlightBase(
  device: Device,
  onCompositionChange: (c: Composition | null) => void,
  onNext: () => void,
): PreFlightBaseResult {
  const { validate } = useResponseValidator()
  const { t } = useTranslation('app')
  const { enqueueSnackbar } = useSnackbar()
  const [deviceRoles, setDeviceRoles] = useState<DeviceRole[]>([])
  const [deviceRolesLoading, setDeviceRolesLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>()
  const [selectedYearsInterval, setSelectedYearsInterval] = useState<
    number | undefined
  >()

  const [composition, setComposition] = useState<Composition | null>(null)

  // Guards against duplicate fetches
  const loadedDeviceRef = useRef<string | null>(null)
  const lastCompositionFetchRef = useRef<string | null>(null)

  const supportedCountries = getSupportedCountries(device.type)
  const hasBoard = deviceHasBatteryBoard(device)
  const effectiveYears = selectedYearsInterval ?? device.yearsBetweenMaintenance
  const minInstalledDate = getMinInstallationDate(effectiveYears)

  const effectiveCountry = useMemo(() => {
    if (selectedCountry !== undefined) return selectedCountry
    return supportedCountries.includes(device.country)
      ? device.country
      : (supportedCountries[0] ?? '')
  }, [selectedCountry, device.country, supportedCountries])

  const countryItems = useMemo(
    () =>
      supportedCountries.map((code) => ({
        id: code,
        label:
          countries.getName(code, i18n.language, { select: 'official' }) ??
          code,
      })),
    [supportedCountries],
  )

  // Only auto-assign when the backend leaves no choice; a selection is
  // required when multiple roles exist and none is set on the composition
  const defaultDeviceRole =
    deviceRoles.length === 1 ? deviceRoles[0].deviceRoleCode : ''
  const deviceRoleRequired =
    deviceRoles.length > 1 && !composition?.composition?.deviceRoleCode

  useEffect(() => {
    const fetchRoles = async () => {
      if (loadedDeviceRef.current === device.uid) return
      setDeviceRolesLoading(true)
      loadedDeviceRef.current = device.uid
      try {
        const response = await getDeviceRoles(device.type)
        const validated = await validate<DeviceRoles>(
          DeviceRolesSchema,
          response,
        )
        setDeviceRoles(validated.deviceRoles)
      } catch {
        setDeviceRoles([])
        loadedDeviceRef.current = null
      } finally {
        setDeviceRolesLoading(false)
      }
    }
    void fetchRoles()
  }, [device.uid, device.type, validate])

  const fetchComposition = useCallback(async () => {
    const fetchKey = `${device.uid}:${effectiveCountry}`
    if (lastCompositionFetchRef.current === fetchKey) return
    lastCompositionFetchRef.current = fetchKey
    try {
      const response = await getDeviceComposition(device.uid, effectiveCountry)
      const validated = await validate<Composition>(CompositionSchema, response)
      setComposition(validated)
      onCompositionChange(validated)
    } catch {
      enqueueSnackbar(
        t('app:device.calibration.preFlightStep.configNotAvailable'),
        { variant: 'error', autoHideDuration: 5000 },
      )
      setComposition(null)
      onCompositionChange(null)
      lastCompositionFetchRef.current = null
    }
  }, [
    device.uid,
    effectiveCountry,
    onCompositionChange,
    validate,
    t,
    enqueueSnackbar,
  ])

  useEffect(() => {
    void fetchComposition()
  }, [fetchComposition])

  const refreshComposition = useCallback(() => {
    lastCompositionFetchRef.current = null
    void fetchComposition()
  }, [fetchComposition])

  const submitForm = useCallback(
    async (options: PreFlightSubmitOptions) => {
      setIsSaving(true)
      try {
        const resolvedRoleCode =
          options.deviceRoleCode || defaultDeviceRole || undefined
        const promises: Promise<unknown>[] = [
          patchDevice(device, {
            country: options.country,
            ...(resolvedRoleCode ? { deviceRoleCode: resolvedRoleCode } : {}),
          }),
        ]

        if (options.yearsBetweenMaintenance) {
          promises.push(
            updateServiceInterval(
              device.uid,
              Number.parseInt(options.yearsBetweenMaintenance, 10),
            ),
          )
        }

        if (hasBoard && options.installedDate) {
          const parsed = parse(options.installedDate, DATE_FORMAT, new Date())
          promises.push(updateBatteryBoardDate(device.uid, parsed))
        }

        if (options.onExtraSubmit) {
          promises.push(options.onExtraSubmit())
        }

        await Promise.all(promises)
        onNext()
      } catch (error) {
        const axiosError = error as {
          response?: { status?: number; data?: unknown }
        }
        const status = axiosError.response?.status ?? 0
        const data = axiosError.response?.data

        const extractMessage = (d: unknown): string | undefined => {
          if (typeof d === 'string' && d.length > 0) return d
          if (d && typeof d === 'object' && 'message' in d)
            return String(d.message)
          return undefined
        }

        if (status >= 500) {
          enqueueSnackbar(extractMessage(data) ?? `Server error: ${status}`, {
            variant: 'error',
          })
        } else if (status === 409) {
          enqueueSnackbar(extractMessage(data) ?? `Request failed: ${status}`, {
            variant: 'error',
          })
        } else if (status === 0) {
          enqueueSnackbar(
            error instanceof Error ? error.message : 'Request failed',
            { variant: 'error' },
          )
        }
        // setActiveStep deliberately not called - user stays on preflight
      } finally {
        setIsSaving(false)
      }
    },
    [defaultDeviceRole, device, hasBoard, onNext, enqueueSnackbar],
  )

  const handleCountryChange = useCallback((country: string) => {
    setSelectedCountry(country)
    lastCompositionFetchRef.current = null
  }, [])

  const handleYearsIntervalChange = useCallback((years: number) => {
    setSelectedYearsInterval(years)
  }, [])

  return {
    composition,
    refreshComposition,
    deviceRoles,
    deviceRolesLoading,
    isSaving,
    effectiveCountry,
    effectiveYears,
    countryItems,
    defaultDeviceRole,
    deviceRoleRequired,
    hasBoard,
    minInstalledDate,
    submitForm,
    handleCountryChange,
    handleYearsIntervalChange,
  }
}
